/**
 * Save-time CSS inliner for ad assets.
 *
 * Banner HTML is authored with the admin site's stylesheets available
 * (Tailwind utility classes etc.), but it must be served self-contained —
 * partner sites don't share those stylesheets. At save we mount the markup
 * in a hidden in-document sandbox, resolve every element's computed style,
 * and bake the meaningful properties into style="" attributes, dropping the
 * class names. Media-query behavior doesn't survive inlining (computed
 * style is single-viewport) — responsive banners should rely on flex/wrap
 * and percentage sizing.
 */

//properties worth baking in — visual + layout + typography. Copying all
//~340 computed props would bloat every node with browser defaults.
const PROPS = [
  "display", "position", "top", "right", "bottom", "left", "z-index",
  "flex-direction", "flex-wrap", "flex-grow", "flex-shrink", "flex-basis",
  "align-items", "align-self", "justify-content", "gap", "row-gap", "column-gap",
  "grid-template-columns", "grid-template-rows",
  "width", "min-width", "max-width", "height", "min-height", "max-height",
  "margin-top", "margin-right", "margin-bottom", "margin-left",
  "padding-top", "padding-right", "padding-bottom", "padding-left",
  "border-top-width", "border-right-width", "border-bottom-width", "border-left-width",
  "border-top-style", "border-right-style", "border-bottom-style", "border-left-style",
  "border-top-color", "border-right-color", "border-bottom-color", "border-left-color",
  "border-top-left-radius", "border-top-right-radius",
  "border-bottom-left-radius", "border-bottom-right-radius",
  "background-color", "background-image", "background-size",
  "background-position", "background-repeat",
  "color", "font-family", "font-size", "font-weight", "font-style",
  "line-height", "letter-spacing", "text-align", "text-decoration-line",
  "text-transform", "white-space",
  "opacity", "overflow-x", "overflow-y", "box-shadow", "cursor",
  "object-fit", "object-position", "transform", "transition-property",
]

/**
 * Inline the computed styles of `html` and return self-contained markup.
 * Must run in the browser (uses getComputedStyle against live DOM).
 */
export function inlineCSS(html) {
  if (typeof window === "undefined" || !html) return html

  //live sandbox — rendered (not display:none, which zeroes layout-dependent
  //computed values) but invisible + out of flow
  const sandbox = document.createElement("div")
  sandbox.style.cssText =
    "position:fixed;left:-10000px;top:0;width:900px;visibility:hidden;pointer-events:none;"
  sandbox.innerHTML = html
  document.body.appendChild(sandbox)

  //defaults baseline per tag — skip properties that match the browser default
  const defaultsCache = new Map()
  const defaultsFor = (tag) => {
    if (defaultsCache.has(tag)) return defaultsCache.get(tag)
    const probe = document.createElement(tag)
    sandbox.appendChild(probe)
    const cs = window.getComputedStyle(probe)
    const snapshot = {}
    for (const p of PROPS) snapshot[p] = cs.getPropertyValue(p)
    probe.remove()
    defaultsCache.set(tag, snapshot)
    return snapshot
  }

  try {
    const walk = (el) => {
      if (el.nodeType !== 1) return
      const cs = window.getComputedStyle(el)
      const defaults = defaultsFor(el.tagName.toLowerCase())
      //existing inline styles win — collect them first
      const existing = el.getAttribute("style") || ""
      const decls = []
      for (const p of PROPS) {
        const v = cs.getPropertyValue(p)
        if (!v || v === defaults[p]) continue
        if (existing.includes(p)) continue
        decls.push(`${p}:${v}`)
      }
      if (decls.length) {
        el.setAttribute("style", existing ? `${existing};${decls.join(";")}` : decls.join(";"))
      }
      el.removeAttribute("class")
      for (const child of [...el.children]) walk(child)
    }
    for (const child of [...sandbox.children]) walk(child)
    return sandbox.innerHTML
  } finally {
    sandbox.remove()
  }
}

export default inlineCSS
